import { Button, Input, Select, Space, TableProps, Tooltip } from "antd";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import StatusItem from "../../../components/common/StatusItem";
import TitleBar from "../../../components/common/TitleBar";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import { GetAllJobPostParamsType } from "../../../features/organization/job/job.type";
import {
  useDeleteJobPostMutation,
  useGetAllJobPostQuery,
} from "../../../features/organization/job/jobApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { countParPageSerialNumber } from "../../../utils/number";
import { valueOrNa } from "../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../utils/utils";
import { jobStatusList, jobTypeList } from "./_data/data";
import CopyIcon from "./_icons/CopyIcon";
import LockIcon from "./_icons/LockIcon";
import WebIcon from "./_icons/WebIcon";
import "./job.css";

export default function JobList() {
  const { open, handleOpen, handleClose } = useModal<number, "DELETE">();
  const {
    page,
    limit,
    filter,
    keyword,
    handleFilter,
    handleLimit,
    handlePagination,
    handleSearch,
  } = useTable<GetAllJobPostParamsType>();
  const { user } = useAppSelector((state) => state.auth);
  const { isLoading, isFetching, isError, error, data } = useGetAllJobPostQuery(
    {
      params: {
        ...filter,
        search: keyword,
        business_id: user?.business_id,
      },
    }
  );
  const [
    deleteJobPost,
    {
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
      isError: isDeleteError,
      error: deleteError,
    },
  ] = useDeleteJobPostMutation();
  useError(isError, error, "Job post get fail");
  useError(isDeleteError, deleteError, "Job post delete fail");

  const domainURL = window.location.origin;

  const handleCopy = (id: number) => {
    const link = `${domainURL}/bondhu-cms/v1/job/v1/details/${id}`;

    navigator.clipboard.writeText(link).then(() => {
      toast("success", "URL Copied");
    });
  };

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Job post ID",
      dataIndex: "job_unique_id",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      render: (val) => <div className="max-w-[300px]">{valueOrNa(val)}</div>,
    },
    {
      title: "Created at",
      dataIndex: "created_at",
      render: (val) => (val ? formatDate(val) : valueOrNa(val)),
    },
    {
      title: "Job Type",
      dataIndex: "contract_type",
      align: "center",
      render: (val) =>
        val ? <StatusItem value={val} list={jobTypeList} /> : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val) => <StatusItem value={val} list={jobStatusList} />,
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
      align: "center",
      render: (val) => (
        <>
          {val === "public" ? (
            <span className={`${`status !bg-purple-100 !text-purple-600`}`}>
              <WebIcon /> <span className="ml-1">Public</span>
            </span>
          ) : (
            <span className={`${`status  !bg-teal-50 !text-green-600`}`}>
              <LockIcon /> <span className="ml-1">Private</span>
            </span>
          )}
        </>
      ),
    },
    {
      title: "Action",
      // dataIndex: "id",
      align: "end",
      width: 130,
      render: (val, record) => (
        <Space align="center">
          {record?.visibility === "private" && (
            <Tooltip title="Copy URL">
              <Button
                className="action-copy"
                size="small"
                icon={<CopyIcon />}
                onClick={() => handleCopy(val?.id)}
              />
            </Tooltip>
          )}
          <Link to={`view/${val?.id}`}>
            <Tooltip title="View">
              <Button
                className="action-view"
                size="small"
                icon={<ViewIcon />}
                onClick={() =>
                  handleOpen({
                    type: "DELETE",
                    data: val?.id,
                  })
                }
              />
            </Tooltip>
          </Link>
          {val?.status === "posted" ? (
            <Link to={`update/${val?.id}`}>
              <Tooltip title="Edit">
                <Button
                  className="action-edit"
                  size="small"
                  icon={<EditIcon />}
                />
              </Tooltip>
            </Link>
          ) : null}
          {!val?.is_deleted && val?.status === "posted" && (
            <Tooltip title="Delete">
              <Button
                className="action-delete"
                size="small"
                icon={<DeleteIcon />}
                onClick={() =>
                  handleOpen({
                    type: "DELETE",
                    data: val?.id,
                  })
                }
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (isDeleteSuccess) {
      toast("success", "Job post deleted successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteSuccess]);

  return (
    <SidebarLayout title="Post a Job">
      <TitleBar title="Job List">
        <Link to="add">
          <Button icon={<AddIcon />} type="primary" className="sm-button !px-3">
            Post a job
          </Button>
        </Link>
      </TitleBar>

      {open?.type === "DELETE" && (
        <ConfirmModal
          width={400}
          subTitle="Are you sure you want to delete this job post?"
          title="Delete This Job Post?"
          handleClose={handleClose}
          open={open?.open}
          data={open?.data}
          okLoading={isDeleteLoading}
          handleOk={(values) => {
            deleteJobPost(values!);
          }}
        />
      )}

      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total}
        dataSource={addKeyInArray(data?.data?.jobs)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />

            <Select
              allowClear
              showSearch
              placeholder="Job Type"
              filterOption={selectSearchOption}
              options={selectArrayOption(jobTypeList, "label", "value")}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              value={filter.contract_type}
              onChange={(val) => handleFilter("contract_type", val)}
              popupMatchSelectWidth={120}
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={selectArrayOption(jobStatusList, "label", "value")}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              value={filter.status}
              onChange={(val) => handleFilter("status", val)}
              popupMatchSelectWidth={120}
            />
          </>
        }
      />
    </SidebarLayout>
  );
}
